import React, { useState, useEffect } from 'react';
import { Plus, Printer, User } from 'lucide-react';
import FullView2 from './Calendar/FullView2';
import PrintView from './Calendar/PrintView';
import appointmentService from '../services/appointmentService';
import useTechnicianFilter from '../hooks/useTechnicianFilter';
import TechnicianFilter from './TechnicianFilter';
import useMobileHeaderActions from '../hooks/useMobileHeaderActions';
import { getFranchiseeIdForCRUD } from '../App/services/appHelpers';
import ConfirmDialog from './ConfirmDialog';
import useConfirm from '../hooks/useConfirm';
import { useToast } from './Toast';

const Calendar = ({ userProfile, onAddAppointment, onEditAppointment }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const { confirmDialog, confirm } = useConfirm();
  const toast = useToast();
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [showPrintView, setShowPrintView] = useState(false);

  // Shared technician filter
  const techFilter = useTechnicianFilter(userProfile);

  useMobileHeaderActions(
    techFilter.showFilter ? <TechnicianFilter {...techFilter} compact /> : null,
    [techFilter.showFilter, techFilter.selectedTechnician, techFilter.techGroups]
  );

  // Load appointments
  useEffect(() => {
    loadAppointments();
  }, [currentDate, techFilter.selectedTechnician, refreshTrigger]);

  const loadAppointments = async () => {
    if (!userProfile) return;

    try {
      // Suppress loading spinner if appointments already on screen
      if (appointments.length === 0) {
        setLoading(true);
      }

      const franchiseeId = techFilter.showFilter
        ? techFilter.getFilteredFranchiseeId()
        : getFranchiseeIdForCRUD(userProfile);
      const branchId = techFilter.getSelectedBranchId();

      // Full view: +/-60 days
      const rangeStart = new Date(currentDate);
      rangeStart.setDate(rangeStart.getDate() - 60);
      const rangeEnd = new Date(currentDate);
      rangeEnd.setDate(rangeEnd.getDate() + 60);

      const params = {
        date_from: rangeStart.toISOString().split('T')[0],
        date_to: rangeEnd.toISOString().split('T')[0],
        limit: 1000,
        sortBy: 'scheduled_date',
        sortOrder: 'ASC',
        ...(branchId ? { branch_id: branchId } : {})
      };

      const response = await appointmentService.getAppointments(franchiseeId, params);
      setAppointments(response.appointments || []);
    } catch (error) {
      console.error('Error loading appointments:', error);
    } finally {
      setLoading(false);
    }
  };

  // Navigation handlers
  const handleNavigate = (direction) => {
    const newDate = new Date(currentDate);
    if (direction === 'prev') {
      newDate.setDate(newDate.getDate() - 7);
    } else if (direction === 'next') {
      newDate.setDate(newDate.getDate() + 7);
    }
    setCurrentDate(newDate);
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleTimeSlotClick = (dateTime) => {
    onAddAppointment({ initialDateTime: dateTime });
    setTimeout(() => setRefreshTrigger(prev => prev + 1), 500);
  };

  const handleAppointmentClick = (appointment) => {
    onEditAppointment(appointment);
    setTimeout(() => setRefreshTrigger(prev => prev + 1), 500);
  };

  const showToast = (message, type = 'success') => {
    if (type === 'error') toast.error(message);
    else toast.success(message);
  };

  const fmtDuration = (m) => {
    if (m < 60) return `${m}min`;
    const h = Math.floor(m / 60);
    const r = m % 60;
    return r > 0 ? `${h}h ${r}m` : `${h}h`;
  };

  const fmtTime = (d) => {
    let h = d.getHours();
    const m = String(d.getMinutes()).padStart(2, '0');
    const ampm = h >= 12 ? 'pm' : 'am';
    h = h % 12 || 12;
    return m === '00' ? `${h}${ampm}` : `${h}:${m}${ampm}`;
  };

  const fmtDate = (d) => {
    const day = d.getDate();
    const suffix = [,'st','nd','rd'][day % 10 > 3 ? 0 : (day % 100 - day % 10 !== 10) * (day % 10)] || 'th';
    const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    return `${day}${suffix} ${months[d.getMonth()]} ${d.getFullYear()}`;
  };

  const handleAppointmentDrop = async (appointment, newDateTime, newDuration = null) => {
    if (!userProfile) return;

    const oldDateTime = new Date(appointment.scheduled_date);

    // If dropped in the same position with no duration change, do nothing
    if (oldDateTime.getTime() === newDateTime.getTime() && !newDuration) return;

    // Use new duration if provided, otherwise keep existing
    const duration = newDuration || appointment.estimated_duration || 60;

    // Convert to MySQL DATETIME format (YYYY-MM-DD HH:MM:SS)
    const formatMySQLDateTime = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    };

    // Duration-only resize (same time, different duration) — save immediately, no confirm
    const isDurationOnlyChange = oldDateTime.getTime() === newDateTime.getTime() && newDuration;

    if (!isDurationOnlyChange) {
      // Check for conflicts
      const conflicts = checkConflicts(newDateTime, duration, appointment.assigned_technician_id, appointment.id);

      let message = `From: ${fmtDate(oldDateTime)} at ${fmtTime(oldDateTime)}\nTo: ${fmtDate(newDateTime)} at ${fmtTime(newDateTime)}`;

      if (conflicts.length > 0) {
        const conflictMessages = conflicts.map(c => {
          const cDate = new Date(c.scheduled_date);
          return `${fmtTime(cDate)} — ${c.customer_name}`;
        }).join(', ');
        message += `\n\nConflict: Overlaps with ${conflictMessages}`;
      }

      const confirmed = await confirm({
        title: `Reschedule "${appointment.customer_name}"?`,
        message,
        variant: conflicts.length > 0 ? 'warning' : 'question',
        confirmLabel: 'Reschedule'
      });
      if (!confirmed) return;
    }

    try {
      const franchiseeId = getFranchiseeIdForCRUD(userProfile, appointment.franchisee_id);

      // Calculate new end date based on duration
      const scheduledEndDate = new Date(newDateTime.getTime() + (duration * 60000));

      const updateData = {
        scheduled_date: formatMySQLDateTime(newDateTime),
        scheduled_end_date: formatMySQLDateTime(scheduledEndDate)
      };

      // Include duration if it changed
      if (newDuration && newDuration !== appointment.estimated_duration) {
        updateData.estimated_duration = newDuration;
      }

      await appointmentService.updateAppointment(franchiseeId, appointment.id, updateData);

      // Update appointment in local state without triggering reload
      setAppointments(prevAppointments =>
        prevAppointments.map(apt =>
          apt.id === appointment.id
            ? {
                ...apt,
                scheduled_date: updateData.scheduled_date,
                scheduled_end_date: updateData.scheduled_end_date,
                estimated_duration: updateData.estimated_duration || apt.estimated_duration
              }
            : apt
        )
      );

      if (isDurationOnlyChange) {
        showToast(`Duration updated to ${fmtDuration(newDuration)}`);
      } else {
        showToast(`Moved to ${fmtDate(newDateTime)}, ${fmtTime(newDateTime)}`);
      }
    } catch (error) {
      console.error('Error rescheduling appointment:', error);
      showToast('Failed to save changes', 'error');
    }
  };

  const checkConflicts = (newDateTime, duration, technicianId, excludeAppointmentId = null) => {
    if (!technicianId || !duration) return [];

    const appointmentStart = newDateTime.getTime();
    const appointmentEnd = appointmentStart + (duration * 60 * 1000);

    return appointments.filter(apt => {
      if (apt.id === excludeAppointmentId) return false;
      if (apt.assigned_technician_id !== technicianId) return false;
      if (apt.status === 'cancelled') return false;

      const aptStart = new Date(apt.scheduled_date).getTime();
      const aptEnd = aptStart + ((apt.estimated_duration || 60) * 60 * 1000);

      return (appointmentStart < aptEnd && appointmentEnd > aptStart);
    });
  };

  // Quick status change handler (for one-tap status transitions on cards)
  const handleQuickStatus = async (appointment, newStatus) => {
    try {
      const franchiseeId = getFranchiseeIdForCRUD(userProfile, appointment.franchisee_id);
      await appointmentService.updateAppointment(franchiseeId, appointment.id, { status: newStatus });
      setAppointments(prev => prev.map(apt =>
        apt.id === appointment.id ? { ...apt, status: newStatus } : apt
      ));
      const label = newStatus.charAt(0).toUpperCase() + newStatus.slice(1);
      showToast(`Marked as ${label}`);
    } catch (error) {
      console.error('Error updating appointment status:', error);
      showToast('Failed to update status', 'error');
    }
  };

  return (
    <div className="h-screen flex flex-col bg-gray-100 pt-0 md:pt-0">
      {/* Compact Header */}
      <div className="bg-gradient-to-r from-pest-blue to-pest-teal text-white shadow-lg">
        <div className="px-2 sm:px-4 py-2 flex flex-wrap items-center gap-2 sm:gap-3">
          {/* Spacer */}
          <div className="flex-1 min-w-0" />

          {/* Right actions */}
          <div className="flex items-center space-x-1 sm:space-x-2">
            <button
              onClick={handleToday}
              className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-2 sm:px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors"
            >
              Today
            </button>
            <button
              onClick={() => setShowPrintView(true)}
              className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white p-1.5 rounded-lg transition-colors"
              title="Print"
            >
              <Printer size={16} />
            </button>
            <button
              onClick={() => onAddAppointment({})}
              className="btn btn-sm btn-primary"
            >
              <Plus size={14} />
              <span className="hidden md:inline">New</span>
            </button>
          </div>

          {/* Technician Selector - desktop only (mobile uses header) */}
          {techFilter.showFilter && (
            <div className="hidden md:flex items-center space-x-2 w-auto">
              <User size={14} className="text-white opacity-70 hidden sm:block" />
              <select
                value={techFilter.selectedTechnician}
                onChange={(e) => techFilter.setSelectedTechnician(e.target.value)}
                className="border border-white border-opacity-30 rounded-lg px-2 sm:px-3 py-1.5 text-xs sm:text-sm text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 flex-1 sm:flex-none sm:min-w-[200px]"
              >
                {techFilter.isAdmin && <option value="all">All Technicians</option>}
                {techFilter.techGroups.map(group => (
                  <optgroup key={group.id} label={group.company || group.name}>
                    <option value={`f:${group.id}`}>{group.name} (All)</option>
                    <option value={`${group.id}:${group.branchId}`}>{group.name}</option>
                    {group.technicians.map(tech => (
                      <option key={tech.id} value={`${group.id}:${tech.branchId}`}>{tech.name}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Calendar View */}
      <div className="flex-1 overflow-hidden bg-white">
        {loading && appointments.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-gray-500 text-lg">Loading appointments...</div>
          </div>
        ) : (
          <FullView2
            currentDate={currentDate}
            appointments={appointments}
            onAppointmentClick={handleAppointmentClick}
            onTimeSlotClick={handleTimeSlotClick}
            onNavigate={handleNavigate}
            onAppointmentDrop={handleAppointmentDrop}
            onQuickStatus={handleQuickStatus}
          />
        )}
      </div>

      {/* Legend */}
      <div className="px-4 py-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
        <span className="font-medium text-gray-600">Status:</span>
        <div className="flex items-center space-x-1"><div className="w-3 h-3 bg-blue-500 rounded-full"></div><span>Scheduled</span></div>
        <div className="flex items-center space-x-1"><div className="w-3 h-3 bg-blue-800 rounded-full"></div><span>Confirmed</span></div>
        <div className="flex items-center space-x-1"><div className="w-3 h-3 bg-green-600 rounded-full"></div><span>Completed</span></div>
        <div className="flex items-center space-x-1"><div className="w-3 h-3 bg-gray-400 rounded-full"></div><span>Cancelled</span></div>
      </div>

      {/* Print View Modal */}
      {showPrintView && (
        <PrintView
          appointments={appointments}
          selectedDate={currentDate}
          technicianName={
            techFilter.selectedTechnician === 'all' ? null
              : techFilter.selectedTechnician.startsWith('f:')
                ? techFilter.techGroups.find(g => g.id === techFilter.selectedTechnician.slice(2))?.name
                : (() => {
                    const branchId = techFilter.getSelectedBranchId();
                    for (const g of techFilter.techGroups) {
                      if (String(g.branchId) === branchId) return g.name;
                      const tech = g.technicians.find(t => String(t.branchId) === branchId);
                      if (tech) return tech.name;
                    }
                    return null;
                  })()
          }
          onClose={() => setShowPrintView(false)}
        />
      )}
      <ConfirmDialog {...confirmDialog} />

    </div>
  );
};

export default Calendar;
